import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from '@mui/material';
import Radio from '@mui/material/Radio';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

export const ModalPlanOnboarding = ({
  open,
  onClose,
  type = 'upgrade',
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  type?: 'upgrade' | 'downgrade' | 'unlock' | 'buy-extra-credits';
  onConfirm?: () => Promise<void> | void;
}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  const handleClose = () => {
    setPlanUpgradedWithSuccess(false);
    setPlanDowngradedScheduledWithSuccess(false);
    setIsLoading(false);
    onClose();
  };

  const [title, setTitle] = useState('Plan upgrade');
  const [description, setDescription] = useState('');
  const [modalLabel, setModalLabel] = useState('Plan upgrade');
  const [actionButtonText, setActionButtonText] = useState('Confirm');
  const [cancelButtonText, setCancelButtonText] = useState('Cancel');
  const [isLoading, setIsLoading] = useState(false);

  const [planUpgradedWithSuccess, setPlanUpgradedWithSuccess] = useState(false);
  const [
    planDowngradedScheduledWithSuccess,
    setPlanDowngradedScheduledWithSuccess,
  ] = useState(false);

  // Reset states when modal opens/closes or type changes
  useEffect(() => {
    if (!open) {
      setPlanUpgradedWithSuccess(false);
      setPlanDowngradedScheduledWithSuccess(false);
      setIsLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (type === 'upgrade') {
      setModalLabel('Plan upgrade');
      setTitle('Confirm your plan upgrade');
      setDescription(
        "You'll get immediate access to your new plan's features and a higher credit limit. You'll be charged a prorated amount of $[price_difference] for the remainder of this billing cycle."
      );
      setActionButtonText('Confirm');
      setCancelButtonText('Cancel');
    } else if (type === 'downgrade') {
      setModalLabel('Plan downgrade');
      setTitle('Confirm your plan downgrade');
      setDescription(
        "Your plan will be downgraded at the start of your next billing cycle on [next_billing_cycle_date]. Until then, you'll continue to have full access to your current plan's features."
      );
      setActionButtonText('Confirm');
      setCancelButtonText('Cancel');
    } else if (type === 'unlock') {
      setModalLabel('Feature unavailable on current plan');
      setTitle('Unlock this feature with an upgrade');
      setDescription(
        "This feature isn't available on your current plan, but you can start using it right away by upgrading to a higher-tier plan. Upgrade now to get instant access."
      );
      setActionButtonText('Manage plans');
      setCancelButtonText('Cancel');
    } else if (type === 'buy-extra-credits') {
      setModalLabel('Buy extra credits');
      setTitle('Choose the credit package that fits your needs');
      setDescription(
        'Running low on credits? Select one of the packages below to keep your assistant working without interruptions. Credits are added instantly to your account.'
      );
      setActionButtonText('Buy extra credits');
      setCancelButtonText('Cancel');
    }
  }, [type]);

  useEffect(() => {
    if (planUpgradedWithSuccess && type === 'upgrade') {
      setModalLabel('Plan upgrade');
      setTitle('Your plan has been upgraded 🎉');
      setDescription(
        "You now have access to all the features of your new plan. Your total credit limit for this billing cycle is [total_credits]. You're all set to keep growing with Oppy!"
      );
      setActionButtonText('Close');
      setCancelButtonText('hidden');
    } else if (planDowngradedScheduledWithSuccess && type === 'downgrade') {
      setModalLabel('Plan downgrade');
      setTitle('Downgrade scheduled ✅');
      setDescription(
        "Your plan will be downgraded on [next_billing_cycle_date] at the start of your next billing cycle. You'll continue to enjoy your current plan's features until then."
      );
      setActionButtonText('Close');
      setCancelButtonText('hidden');
    }
  }, [planUpgradedWithSuccess, planDowngradedScheduledWithSuccess, type]);

  const handleConfirm = async () => {
    if (planUpgradedWithSuccess || planDowngradedScheduledWithSuccess) {
      handleClose();
      return;
    }

    setIsLoading(true);
    try {
      if (onConfirm) {
        await onConfirm();
      }
      // Mostra o loading por 2 segundos
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (error) {
      console.error('Error confirming action:', error);
    } finally {
      setIsLoading(false);
      // Só troca para o estado de sucesso depois do loading
      if (type === 'upgrade') {
        setPlanUpgradedWithSuccess(true);
      } else if (type === 'downgrade') {
        setPlanDowngradedScheduledWithSuccess(true);
      } else {
        handleClose();
      }
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: {
            xs: '90%', // Mobile: 90% of screen width
            sm: '80%', // Small tablets: 80% of screen width
            md: '70%', // Medium tablets: 70% of screen width
            lg: '530px', // Desktop: fixed 530px
          },
          maxWidth: 530,
          backgroundColor: darkMode ? theme.palette.background.paper : 'white',
          pt: { xs: 2, sm: 2, md: 2 },
          pl: { xs: 2, sm: 3, md: 3 },
          pr: { xs: 2, sm: 3, md: 3 },
          pb: { xs: 2, sm: 3, md: 3 },
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxHeight: {
            xs: '90vh', // Mobile: 90% of viewport height
            sm: '85vh', // Small tablets: 85% of viewport height
            md: '80vh', // Medium tablets: 80% of viewport height
            lg: 'auto', // Desktop: auto height
          },
          overflow: 'auto', // Allow scrolling if content is too long
          border: `1px solid ${
            darkMode ? theme.palette.divider : theme.palette.grey[200]
          }`,
          boxShadow: darkMode
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{
              fontSize: { xs: 12, sm: 13, md: 14 },
              lineHeight: { xs: '20px', sm: '22px', md: '24px' },
              fontWeight: 500,
              fontFamily: 'Space Grotesk, sans-serif',
              color: darkMode ? theme.palette.text.primary : '#111827',
            }}
          >
            {modalLabel}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{
              color: theme.palette.primary.main,
              fontSize: { xs: 12, sm: 13, md: 14 },
              fontWeight: 500,
              minWidth: 0,
              width: { xs: 20, sm: 22, md: 24 },
              height: { xs: 20, sm: 22, md: 24 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: darkMode
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }} />
          </Button>
        </Box>
        <Typography
          id="modal-modal-description"
          sx={{
            mt: { xs: 3, sm: 3, md: 4 },
            mb: { xs: 1, sm: 1, md: 1 },
            fontSize: { xs: 16, sm: 18, md: 20 },
            lineHeight: { xs: '24px', sm: '28px', md: '32px' },
            fontWeight: 500,
            fontFamily: 'Space Grotesk, sans-serif',
            color: darkMode ? theme.palette.text.primary : '#111827',
            textAlign: 'center',
            width: { xs: '95%', sm: '90%', md: '80%' },
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 12, sm: 13, md: 14 },
            fontWeight: 400,
            fontFamily: 'Space Grotesk, sans-serif',
            color: darkMode ? theme.palette.text.secondary : '#424242',
            textAlign: 'center',
            lineHeight: { xs: '20px', sm: '22px', md: '24px' },
            width: '100%',
            maxWidth: { xs: '100%', sm: '90%', md: 400 },
            margin: '0 auto',
            px: { xs: 1, sm: 0, md: 0 },
          }}
        >
          {description}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            mt: { xs: 3, sm: 3, md: 4 },
          }}
        >
          {type === 'buy-extra-credits' && <CreditPackageSelector />}
        </Box>
        <Box
          sx={{
            mt: { xs: 2, sm: 2, md: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'flex-end',
            gap: { xs: 1, sm: 2 },
            width: '100%',
          }}
        >
          {cancelButtonText !== 'hidden' && (
            <Button
              variant="text"
              onClick={handleClose}
              disabled={isLoading}
              sx={{
                color: darkMode ? '#fff' : '#4949C8',
                fontSize: { xs: 12, sm: 13, md: 14 },
                fontWeight: 500,
                borderRadius: 2,
                padding: { xs: '8px 16px', sm: '10px 20px' },
                width: { xs: '100%', sm: 100 },
                height: { xs: 36, sm: 40 },
                order: { xs: 2, sm: 1 }, // On mobile, cancel button goes below
                '&:hover': {
                  backgroundColor: darkMode
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.04)',
                  borderColor: theme.palette.primary.dark,
                },
              }}
            >
              {cancelButtonText}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isLoading}
            sx={{
              backgroundColor: darkMode
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              color: darkMode ? '#4949C8' : '#fff',
              fontSize: { xs: 12, sm: 13, md: 14 },
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', sm: 'auto' },
              height: { xs: 36, sm: 40 },
              order: { xs: 1, sm: 2 }, // On mobile, confirm button goes first
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              },
              '&:disabled': {
                backgroundColor: darkMode
                  ? theme.palette.grey[700]
                  : theme.palette.grey[400],
                color: darkMode
                  ? theme.palette.grey[400]
                  : theme.palette.grey[600],
              },
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress
                  size={16}
                  style={{ color: theme.palette.primary.main, marginRight: 8 }}
                />
                Processing...
              </>
            ) : (
              actionButtonText
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const creditPackages = [
  { label: 'Small', credits: '5,000 credits', price: 25 },
  { label: 'Medium', credits: '10,000 credits', price: 50 },
  { label: 'Large', credits: '20,000 credit', price: 100 },
];

function CreditPackageSelector() {
  const [selected, setSelected] = useState(0);
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  return (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          mb: 2,
          fontSize: { xs: 13, sm: 14, md: 15 },
          fontWeight: 400,
          color: darkMode ? theme.palette.text.primary : '#111827',
        }}
      >
        Select a credit package:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {creditPackages.map((pkg, idx) => (
          <Box
            key={pkg.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1.5px solid',
              borderColor:
                selected === idx
                  ? theme.palette.primary.main
                  : darkMode
                  ? theme.palette.grey[700]
                  : '#E5E7EB',
              borderRadius: 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1, sm: 1.5 },
              background: darkMode ? theme.palette.background.default : '#fff',
              boxShadow:
                selected === idx
                  ? `0 0 0 2px ${theme.palette.primary.main}22`
                  : 'none',
              cursor: 'pointer',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              '&:hover': {
                borderColor:
                  selected === idx
                    ? theme.palette.primary.dark
                    : darkMode
                    ? theme.palette.grey[600]
                    : theme.palette.grey[400],
              },
            }}
            onClick={() => setSelected(idx)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Radio
                checked={selected === idx}
                onChange={() => setSelected(idx)}
                value={pkg.label}
                name="credit-package"
                sx={{
                  color: theme.palette.primary.main,
                  '&.Mui-checked': {
                    color: "#4949C8",
                  },
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: 14, sm: 15, md: 16 },
                    color: darkMode ? theme.palette.text.primary : '#111827',
                  }}
                >
                  {pkg.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: darkMode ? theme.palette.text.secondary : '#6B7280',
                  }}
                >
                  {pkg.credits}
                </Typography>
              </Box>
            </Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: 14, sm: 15, md: 16 },
                color: darkMode ? theme.palette.text.primary : '#111827',
              }}
            >
              ${pkg.price}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
